import "./_dropdown.scss";
import React, {MouseEventHandler, PropsWithChildren, ReactNode} from 'react';
import classNames from 'classnames';
import {Dropdown as XpsDropdown, DropdownItem as XpsDropdownItem} from 'xps-react';
import {NO_OP} from "../../constants/common";

export interface DropdownProps<T extends string> {
    value?: T | null,
    onChange?: (data: DropdownOnChangeData<T>) => void,
    onBlur?: (event: any) => void,
    onKeyPress?: (event: any) => void,
    children?: ReactNode,
    className?: string,
    alignRight?: boolean,
    buttonIcon?: 'right' | 'left' | 'none' | 'only',
    buttonKind?: 'primary' | 'secondary' | 'tertiary' | 'borderless',
    buttonSize?: 'extra-small' | 'small' | 'medium' | 'large',
    buttonAriaLabel?: string,
    defaultPageSize?: number,
    defaultText?: string | number,
    description?: string,
    disabled?: boolean,
    dropdownKind?: 'select' | 'menu',
    dropUp?: boolean,
    dynamicDropAlign?: boolean,
    dynamicDropDirection?: boolean,
    error?: string,
    iconNameClose?: string,
    iconNameOpen?: string,
    id?: string,
    inlineLabel?: boolean,
    label?: string,
    labelWidth?: string | number,
    name?: string,
    nativeOnMobile?: boolean,
    open?: boolean,
    optional?: boolean,
    panelHeight?: string | number,
    panelWidth?: string | number,
    removeMarginTop?: boolean,
    required?: boolean,
    rounded?: boolean,
    searchable?: boolean,
    virtualScroll?: boolean,
    onClick?: MouseEventHandler,
    drop?: string,
    customSearchFilter?: (...args: any[]) => any
    inverse?: boolean;
    danger?: boolean;
    noPadding?: boolean;
}

function addPropsToChildToPreventWarnings(children: ReactNode, parentIsDisabled: boolean): ReactNode {
    return React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
            return child;
        }
        return React.cloneElement(child, {value: "", itemText: "", disabled: parentIsDisabled, ...child.props});
    });
}

/**
 * A generic `Dropdown` component that wraps around the `XpsDropdown` component.
 * It provides additional functionality such as rounded styling, size customization,
 * and the ability to disable the dropdown. It also ensures child components are
 * updated to prevent warnings when the dropdown is disabled.
 *
 * @typeParam T - A string type that represents the generic type for the dropdown.
 *
 * @param children - The child components or elements to be rendered inside the dropdown.
 * @param className - Additional CSS class names to apply to the dropdown.
 * @param iconNameClose - The name of the icon to display when the dropdown is closed. Defaults to `'menu_up'`.
 * @param iconNameOpen - The name of the icon to display when the dropdown is open. Defaults to `'menu_down'`.
 * @param rounded - A boolean indicating whether the dropdown should have rounded corners. Defaults to `false`.
 * @param size - The size of the dropdown. Defaults to `'medium'`.
 * @param disabled - A boolean indicating whether the dropdown is disabled. Defaults to `false`.
 * @param defaultText - The placeholder text to display when no value is selected.
 * @param rest - Additional props to be passed to the `XpsDropdown` component.
 *
 * @returns A JSX element representing the dropdown.
 */
export default function Dropdown<T extends string>({
                                                       children,
                                                       className,
                                                       iconNameClose = 'menu_up',
                                                       iconNameOpen = 'menu_down',
                                                       rounded = false,
                                                       buttonSize = 'medium',
                                                       buttonKind,
                                                       disabled = false,
                                                       inverse = false,
                                                       danger = false,
                                                       noPadding = false,
                                                       ...rest
                                                   }: PropsWithChildren<DropdownProps<T>>) {

    const getClassNames = () => {
        let listOfClasses = "dropdown-button dropdown-button--" + buttonKind;
        if(inverse) listOfClasses += "--inverse";
        if(rounded) listOfClasses += " dropdown-button--rounded";
        if(danger) listOfClasses += " dropdown-button--danger";

        return classNames(listOfClasses, className);
    }

    return <XpsDropdown
        className={getClassNames()}
        iconNameClose={iconNameClose}
        iconNameOpen={iconNameOpen}
        size={buttonSize}
        disabled={disabled}
        {...rest}
    >
        {addPropsToChildToPreventWarnings(children, disabled)}
    </XpsDropdown>
}

export interface DropdownOnChangeData<T> {
    value: T,
    itemText: string,
    index: number,
    fieldID?: string,
    iconName?: string,
    iconType?: string,
}

type DropdownItemProps = {
    itemText?: string,
    value?: string | number,
    onClick?: (e: React.MouseEvent<HTMLElement>) => void,
    id?: string,
    className?: string,
    children?: ReactNode,
    style?: React.CSSProperties,
    disabled?: boolean,
};

export function DropdownItem({
                                 itemText = "",
                                 disabled = false,
                                 onClick = () => NO_OP,
                                 value = "",
                                 ...rest
                             }: Readonly<DropdownItemProps>) {
    return <XpsDropdownItem itemText={itemText} disabled={disabled} onClick={onClick} value={value} {...rest}/>
}
